/**
 * ☰ Admin › Usage › Visitors — who reaches our pages, from where, and how many sign up.
 *
 * `docs/plans/site-visits.md`, built from the owner's approved text mock
 * (2026-10-20). Every page of every host, signed in or not; the location is a
 * country and a city, never an address. The funnel's four numbers are per brand:
 * visitors, sign-ups, the sign-ups we saw arrive, and the sign-ups now paying.
 *
 * Each table sorts by its columns and shows ten rows, then More. Tables scroll
 * sideways inside themselves; the panel never does.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { plainText } from "@qufolio/screener-grid";

import { fetchVisitReport, type VisitReport } from "../api/visits";

const WINDOWS = [7, 30, 90, 365] as const;
const SHOWN = 10;

const BAR: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const NOTE: React.CSSProperties = { color: "var(--qf-text-secondary)", fontSize: 12 };
const MUTED: React.CSSProperties = { color: "var(--qf-text-muted)", fontSize: 12 };
const ERROR: React.CSSProperties = { ...NOTE, color: "var(--qf-negative, #d66)" };
const SCROLL: React.CSSProperties = { overflowX: "auto", maxWidth: "100%" };
const TABLE: React.CSSProperties = { borderCollapse: "collapse", width: "100%", fontSize: 12 };
const TH: React.CSSProperties = {
  textAlign: "left",
  fontWeight: 500,
  color: "var(--qf-text-muted)",
  padding: "4px 8px",
  borderBottom: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
  cursor: "pointer",
  userSelect: "none",
};
const TD: React.CSSProperties = { padding: "4px 8px", whiteSpace: "nowrap", verticalAlign: "top" };
const NUM: React.CSSProperties = { ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const INPUT: React.CSSProperties = {
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "3px 8px",
  fontSize: 12,
};
const SUB: React.CSSProperties = {
  margin: "10px 0 2px",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--qf-text-muted)",
};
const TILES: React.CSSProperties = { display: "flex", gap: 20, flexWrap: "wrap", margin: "8px 0" };
const TILE_VALUE: React.CSSProperties = { fontSize: 20, fontWeight: 600, fontVariantNumeric: "tabular-nums" };
const THREE: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 12,
};

function segment(active: boolean): React.CSSProperties {
  return {
    ...INPUT,
    background: active ? "var(--qf-selection)" : "transparent",
    color: active ? "var(--qf-text)" : "var(--qf-text-secondary)",
    cursor: "pointer",
  };
}

const n = (v: number) => v.toLocaleString();

let regions: Intl.DisplayNames | null = null;
/** `US` → `United States`; the code itself when the browser cannot say. */
export function countryName(code: string | null): string {
  if (!code) return "Unknown";
  try {
    regions ??= new Intl.DisplayNames(["en"], { type: "region" });
    return regions.of(code) ?? code;
  } catch {
    return code;
  }
}

export function AdminVisitors() {
  const [days, setDays] = useState<number>(30);
  const [brand, setBrand] = useState<string>("");
  const [report, setReport] = useState<VisitReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    setError(null);
    fetchVisitReport(days, brand || null)
      .then(setReport)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [days, brand]);
  useEffect(reload, [reload]);

  const rate = report && report.visitors > 0 ? (100 * report.signups) / report.visitors : null;
  const devices = useMemo(() => {
    const rows = report?.devices ?? [];
    const total = rows.reduce((sum, d) => sum + d.visitors, 0);
    return rows.map((d) => ({ ...d, share: total ? Math.round((100 * d.visitors) / total) : 0 }));
  }, [report]);

  return (
    <>
      <div style={BAR}>
        <select
          style={INPUT}
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          aria-label="Brand"
        >
          <option value="">All brands</option>
          {(report?.brands ?? []).map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
        <span role="group" aria-label="Window" style={{ display: "inline-flex", gap: 2 }}>
          {WINDOWS.map((d) => (
            <button key={d} type="button" style={segment(days === d)} onClick={() => setDays(d)}>
              {d}
            </button>
          ))}
        </span>
        <span style={MUTED}>days</span>
        <button type="button" style={INPUT} onClick={reload}>
          Refresh
        </button>
      </div>

      {error && <div style={ERROR}>{plainText(error)}</div>}
      {report === null && !error && <div style={MUTED}>Loading…</div>}

      {report && (
        <>
          <div style={TILES}>
            <Tile label="Visitors" value={n(report.visitors)} />
            <Tile label="Page views" value={n(report.views)} />
            <Tile
              label="Sign-ups"
              value={n(report.signups)}
              title={`${report.signupsSeen} of them were seen arriving; the rest came from a cleared browser, another device or an invite link`}
            />
            <Tile label="Paid" value={n(report.paid)} title="Of the sign-ups, those now paying" />
            <Tile
              label="Visitor → sign-up"
              value={rate === null ? "—" : `${rate.toFixed(1)} %`}
            />
          </div>

          {report.views === 0 ? (
            <div style={NOTE}>No page views in this window.</div>
          ) : (
            <>
              <div style={SUB}>Visitors by day · UTC</div>
              <DayBars days={report.byDay} />

              <div style={THREE}>
                <Ranked
                  title="Countries"
                  column="Country"
                  rows={report.countries.map((c) => ({ key: c.country ?? "?", label: countryName(c.country), ...c }))}
                />
                <Ranked
                  title="Cities"
                  column="City"
                  rows={report.cities.map((c) => ({
                    key: `${c.city}|${c.country}`,
                    label: `${c.city}${c.country ? `, ${c.country}` : ""}`,
                    ...c,
                  }))}
                />
                <Ranked
                  title="Referrers"
                  column="Site"
                  rows={report.referrers.map((r) => ({ key: r.referrer ?? "", label: r.referrer ?? "(direct)", ...r }))}
                />
              </div>

              <Ranked
                title="Pages"
                column="Page"
                rows={report.pages.map((p) => ({ key: `${p.host}${p.path}`, label: `${p.host}${p.path}`, ...p }))}
              />

              {report.campaigns.length > 0 && (
                <Ranked
                  title="Campaigns (utm)"
                  column="Campaign"
                  signups
                  rows={report.campaigns.map((c) => ({
                    key: `${c.campaign}|${c.source}`,
                    label: c.source ? `${c.campaign} · ${c.source}` : c.campaign,
                    ...c,
                  }))}
                />
              )}

              <div style={{ ...SUB, display: "flex", gap: 16, flexWrap: "wrap", textTransform: "none", letterSpacing: 0 }}>
                <span style={{ textTransform: "uppercase", letterSpacing: 0.6 }}>Devices</span>
                {devices.map((d) => (
                  <span key={d.deviceType ?? "?"} style={{ color: "var(--qf-text)", fontSize: 12 }}>
                    {cap(d.deviceType ?? "unknown")} {d.share} %
                  </span>
                ))}
              </div>
            </>
          )}

          <div style={{ ...MUTED, marginTop: 8 }}>
            Every page of every host, signed in or not · location is a country and a city, never an
            address · kept a year · bots are not counted.
          </div>
        </>
      )}
    </>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function Tile({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <div title={title}>
      <div style={MUTED}>{label}</div>
      <div style={TILE_VALUE}>{value}</div>
    </div>
  );
}

/**
 * One bar per UTC day, the empty days included, so the gaps read as gaps.
 * A single series: one hue, no legend, the count on hover.
 */
export function DayBars({ days }: { days: { day: string; visitors: number; views: number }[] }) {
  const filled = useMemo(() => fillDays(days), [days]);
  const max = Math.max(1, ...filled.map((d) => d.visitors));
  return (
    <div
      role="img"
      aria-label={`Visitors by day, at most ${max} in a day`}
      style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 56, margin: "4px 0 6px" }}
    >
      {filled.map((d) => (
        <div
          key={d.day}
          title={`${d.day} · ${d.visitors} visitor${d.visitors === 1 ? "" : "s"}, ${d.views} view${d.views === 1 ? "" : "s"}`}
          style={{ flex: "1 1 0", minWidth: 2, height: "100%", display: "flex", alignItems: "flex-end" }}
        >
          <div
            style={{
              width: "100%",
              height: d.visitors ? `${Math.max(4, (100 * d.visitors) / max)}%` : 1,
              background: d.visitors ? "var(--qf-accent)" : "var(--qf-border)",
              borderRadius: "2px 2px 0 0",
            }}
          />
        </div>
      ))}
    </div>
  );
}

/** Every day from the first to the last, zero where nothing came. */
export function fillDays<T extends { day: string; visitors: number; views: number }>(
  days: T[],
): { day: string; visitors: number; views: number }[] {
  const first = days[0];
  const last = days[days.length - 1];
  if (!first || !last) return [];
  const byDay = new Map(days.map((d) => [d.day, d]));
  const out: { day: string; visitors: number; views: number }[] = [];
  const end = new Date(`${last.day}T00:00:00Z`).getTime();
  for (let t = new Date(`${first.day}T00:00:00Z`).getTime(); t <= end; t += 86_400_000) {
    const day = new Date(t).toISOString().slice(0, 10);
    const hit = byDay.get(day);
    out.push({ day, visitors: hit?.visitors ?? 0, views: hit?.views ?? 0 });
  }
  return out;
}

type RankedRow = { key: string; label: string; views: number; visitors: number; signups?: number };
type RankedSort = "label" | "views" | "visitors" | "signups";

function Ranked({
  title,
  column,
  rows,
  signups,
}: {
  title: string;
  /** The first column's header. */
  column: string;
  rows: RankedRow[];
  signups?: boolean;
}) {
  const [sort, setSort] = useState<RankedSort>("visitors");
  const [all, setAll] = useState(false);
  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) =>
        sort === "label" ? a.label.localeCompare(b.label) : (b[sort] ?? 0) - (a[sort] ?? 0),
      ),
    [rows, sort],
  );
  const shown = all ? sorted : sorted.slice(0, SHOWN);
  const head = (key: RankedSort, label: string, right: boolean) => (
    <th
      style={{ ...TH, textAlign: right ? "right" : "left", color: sort === key ? "var(--qf-text)" : TH.color }}
      onClick={() => setSort(key)}
      title="Click to sort"
    >
      {label}
      {sort === key ? " ▾" : ""}
    </th>
  );
  return (
    <div style={{ minWidth: 0 }}>
      <div style={SUB}>{title}</div>
      {rows.length === 0 ? (
        <div style={NOTE}>None in this window.</div>
      ) : (
        <div style={SCROLL}>
          <table style={TABLE}>
            <thead>
              <tr>
                {head("label", column, false)}
                {head("views", "Views", true)}
                {head("visitors", "Visitors", true)}
                {signups && head("signups", "Sign-ups", true)}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.key}>
                  <td style={{ ...TD, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }} title={r.label}>
                    {r.label}
                  </td>
                  <td style={NUM}>{n(r.views)}</td>
                  <td style={NUM}>{n(r.visitors)}</td>
                  {signups && <td style={NUM}>{n(r.signups ?? 0)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > SHOWN && (
        <button type="button" style={{ ...INPUT, marginTop: 4 }} onClick={() => setAll(!all)}>
          {all ? "Fewer" : `More (${rows.length - SHOWN})`}
        </button>
      )}
    </div>
  );
}
